/**
 * Portal access of a patron: activated or not, and sending the link that lets
 * them choose their password (activation or forgotten password).
 */

import { Loader2, Send } from "lucide-react";
import { useSendPortalInvitation } from "@/api/hooks";
import type { Patron } from "@/api/types";
import { Button } from "@/components/ui/button";

export function PortalAccessCard({ patron }: { patron: Patron }) {
  const send = useSendPortalInvitation(patron.id);
  const canSend = Boolean(patron.email) && patron.canBorrow;

  return (
    <section
      aria-labelledby="portal-access-title"
      className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <h2 id="portal-access-title" className="text-lg font-medium text-gray-900 dark:text-white">
        Accès au portail
      </h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        {patron.hasPortalAccess
          ? "Compte activé : le lecteur se connecte avec son numéro de carte."
          : "Compte non activé : le lecteur ne peut pas encore se connecter au portail."}
      </p>

      {canSend ? (
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          disabled={send.isPending}
          onClick={() => {
            send.mutate();
          }}
        >
          {send.isPending ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
          {patron.hasPortalAccess
            ? "Envoyer un lien de nouveau mot de passe"
            : "Envoyer le lien d'activation"}
        </Button>
      ) : (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {patron.email
            ? "Le compte doit être actif et l'abonnement à jour pour envoyer un lien."
            : "Renseignez l'adresse électronique du lecteur pour lui envoyer un lien."}
        </p>
      )}

      <p role="status" className="mt-2 text-sm text-emerald-700 dark:text-emerald-400">
        {send.isSuccess ? `${send.data.message} Il est valable une heure.` : ""}
      </p>
      {send.error && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {send.error.message}
        </p>
      )}
    </section>
  );
}
